/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  EditingState,
  SortingState,
  IntegratedSorting,
  DataTypeProvider,
  SearchState,
  IntegratedFiltering,
  PagingState,
  IntegratedPaging,
} from '@devexpress/dx-react-grid';
import {
  Grid,
  Table,
  TableHeaderRow,
  TableEditColumn,
  Toolbar,
  SearchPanel,
  DragDropProvider,
  TableColumnReordering,
  TableColumnResizing,
  TableColumnVisibility,
  ColumnChooser,
  PagingPanel,
} from '@devexpress/dx-react-grid-material-ui';
import { Command, PopupEditing, errorAlert } from '../../Shared';
import { SearchTable } from '../../components';
import { getRowId, userroles } from '../../common';
import {
  getEndServices,
  getProducts,
  createEndService,
  updateEndService,
  deleteEndService,
} from '../../graphql';
import { useLazyQuery, useMutation } from '@apollo/client';
import { timeFormatter, userFormatter } from '../../Shared/colorFormat';
import { getColumns } from '../../common/columns';
import { IconButton, Typography } from '@material-ui/core';
import useEmployees from '../../hooks/useEmployees';
import { useUsers } from '../../hooks';
import { getTasks } from '../../graphql/query';
import { TableComponent } from '../components/TableComponent';
import PopupEndService from '../../pubups/PopupEndService';
import HRContext from '../../contexts/hr';
import { useReactToPrint } from 'react-to-print';
import MyIcon from '../components/MyIcon';
import { EndServicePrint } from '../../print/EndServicePrint';

const EndService = forwardRef((props: any, ref) => {
  const { isRTL, words, start, end, theme, company, setAlrt, tempoptions } =
    props;

  const col = getColumns({ isRTL, words });

  // Define refresQuery before mutations to avoid hoisting issues
  const refresQuery = {
    refetchQueries: [
      {
        query: getEndServices,
        variables: {
          start: start ? start.setHours(0, 0, 0, 0) : undefined,
          end: end ? end.setHours(23, 59, 59, 999) : undefined,
        },
      },
      { query: getProducts },
      { query: getTasks },
    ],
  };

  // Define mutations at the very top to avoid hoisting issues
  const [addEndService] = useMutation(createEndService, refresQuery);
  const [editEndService] = useMutation(updateEndService, refresQuery);
  const [deleteEndServiceMutation] = useMutation(deleteEndService, refresQuery);

  const commitChanges = async ({
    deleted,
    changed,
  }: {
    deleted?: any[];
    changed?: any;
  }) => {
    if (deleted) {
      const _id = deleted[0];
      const res = await deleteEndServiceMutation({ variables: { _id } });
      if (res?.data?.deleteEndService?.ok === false) {
        await errorAlert(setAlrt, isRTL);
      } else {
        setRows(rows.filter((row: any) => row._id !== _id));
      }
    }
    if (changed) {
      const [_id, data]: [string, any] = Object.entries(changed)[0];
      const variables = {
        _id,
        ...data,
        contract: data.contractId
          ? {
              contractId: data.contractId,
              contractName: data.contractName,
              contractNameAr: data.contractNameAr,
            }
          : undefined,
        department: data.departmentId
          ? {
              departmentId: data.departmentId,
              departmentName: data.departmentName,
              departmentNameAr: data.departmentNameAr,
              departmentColor: data.departmentColor,
            }
          : undefined,
        employee: data.employeeId
          ? {
              employeeId: data.employeeId,
              employeeName: data.employeeName,
              employeeNameAr: data.employeeNameAr,
            }
          : undefined,
      };
      const res = await editEndService({ variables });
      if (res?.data?.updateEndService?.ok === false) {
        await errorAlert(setAlrt, isRTL);
      }
    }
  };

  const [columns] = useState(
    [
      { name: 'time', title: words?.time },
      { name: 'docNo', title: words?.no },
      col.employee,
      { name: 'title', title: isRTL ? 'العنوان' : 'Title' },
      { name: 'desc', title: words?.description },
      col.userId,
      { name: 'print', title: ' ' },
    ].filter((x) => x),
  );

  const [tableColumnExtensions]: any = useState([
    { columnName: 'time', width: 120 },
    { columnName: 'docNo', width: 120 },
    { columnName: col.employee.name, width: 200 },
    { columnName: 'title', width: 150 },
    { columnName: 'desc', width: 200 },
    { columnName: 'userId', width: 150 },
    { columnName: 'print', width: 80, align: 'center' },
  ]);

  const [rows, setRows] = useState([]);
  const { employeesfull: employees } = useEmployees();
  const { users } = useUsers();
  const [printData, setPrintData] = useState<any>(null);

  const printComponentRef: any = useRef();
  const handleEndServicePrint = useReactToPrint({
    content: () => printComponentRef.current,
    documentTitle: isRTL ? 'نموذج انهاء خدمات' : 'End Service Form',
    removeAfterPrint: true,
  });

  const {
    state: { hiddenColumnNames },
    dispatch,
  } = useContext(HRContext);

  const setHiddenColumnNames = (hiddenColumns: any) => {
    dispatch({ type: 'setHiddenColumnNames', payload: hiddenColumns });
  };

  const [loadEndServices, opData]: any = useLazyQuery(getEndServices, {
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    const variables = {
      start: start ? start.setHours(0, 0, 0, 0) : undefined,
      end: end ? end.setHours(23, 59, 59, 999) : undefined,
    };
    // Always fetch data, with or without date filters
    loadEndServices({
      variables,
    });
  }, [start, end]);

  // Initial fetch on component mount
  useEffect(() => {
    loadEndServices({
      variables: {
        start: undefined,
        end: undefined,
      },
    });
  }, []);

  useImperativeHandle(ref, () => ({
    refresh() {
      if (opData?.refetch) {
        opData.refetch();
      }
    },
  }));

  useEffect(() => {
    if (opData?.data?.getEndServices?.data) {
      const { data } = opData.data.getEndServices;
      setRows(data);
    }
  }, [opData]);

  return (
    <>
      <Grid rows={rows} columns={columns} getRowId={getRowId}>
        <SortingState />
        <EditingState onCommitChanges={commitChanges} />
        <SearchState />
        <PagingState defaultCurrentPage={0} defaultPageSize={10} />
        <IntegratedSorting />
        <IntegratedFiltering />
        <IntegratedPaging />
        <DragDropProvider />
        <Table
          messages={{
            noData: isRTL ? 'لا يوجد بيانات' : 'no data',
          }}
          tableComponent={TableComponent}
          rowComponent={(props: any) => (
            <Table.Row {...props} style={{ height: 60 }}></Table.Row>
          )}
          columnExtensions={tableColumnExtensions}
        />

        <TableColumnReordering
          defaultOrder={[
            'time',
            'docNo',
            col.employee.name,
            'title',
            'desc',
            'userId',
            'print',
          ]}
        />
        <TableColumnResizing defaultColumnWidths={tableColumnExtensions} />

        <TableHeaderRow
          showSortingControls
          titleComponent={({ children }) => {
            return (
              <Typography style={{ fontSize: 14, fontWeight: 'bold' }}>
                {children}
              </Typography>
            );
          }}
        />
        <TableColumnVisibility
          defaultHiddenColumnNames={hiddenColumnNames}
          hiddenColumnNames={hiddenColumnNames}
          onHiddenColumnNamesChange={setHiddenColumnNames}
        />
        <DataTypeProvider
          for={['time']}
          formatterComponent={timeFormatter}
        ></DataTypeProvider>
        <DataTypeProvider
          for={['userId']}
          formatterComponent={(props: any) =>
            userFormatter({ ...props, users })
          }
        ></DataTypeProvider>
        <DataTypeProvider
          for={['print']}
          formatterComponent={({ row }: any) => {
            return (
              <IconButton
                style={{ height: 40 }}
                onClick={() => {
                  const employeeProfile = (employees || []).find(
                    (emp: any) => String(emp?._id) === String(row?.employeeId),
                  );
                  setPrintData({
                    ...row,
                    employeeProfile,
                  });
                  setTimeout(() => {
                    handleEndServicePrint();
                  }, 150);
                }}
              >
                <MyIcon size={20} icon="print"></MyIcon>
              </IconButton>
            );
          }}
        ></DataTypeProvider>
        <TableEditColumn
          showAddCommand={userroles?.sales?.add}
          showEditCommand
          showDeleteCommand
          commandComponent={Command}
        ></TableEditColumn>

        <Toolbar
          rootComponent={(props: any) => (
            <Toolbar.Root
              style={{
                position: 'absolute',
                left: isRTL ? 40 : undefined,
                right: isRTL ? undefined : 40,
                top: 62,
                zIndex: 115,
              }}
              {...props}
            ></Toolbar.Root>
          )}
        />
        <ColumnChooser />
        <PagingPanel
          pageSizes={
            tempoptions?.pageall ? [5, 10, 20, 50, 0] : [5, 10, 20, 50]
          }
        />

        <SearchPanel
          inputComponent={(props: any) => {
            return <SearchTable isRTL={isRTL} {...props}></SearchTable>;
          }}
        />
        <PopupEditing addAction={addEndService} editAction={editEndService}>
          <PopupEndService
            employees={employees}
            company={company}
            theme={theme}
            isRTL={isRTL}
            words={words}
            onRefresh={() => {
              if (opData?.refetch) {
                opData.refetch();
              }
            }}
          ></PopupEndService>
        </PopupEditing>
      </Grid>
      <div style={{ display: 'none' }}>
        <EndServicePrint
          company={company}
          isRTL={isRTL}
          printData={printData}
          ref={printComponentRef}
        />
      </div>
    </>
  );
});

export default EndService;
